// src/modules/app-views/welcome/pages/Init/ApplicationTour.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tour } from 'antd';
import { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import translateX from 'utils/translateX';
import confetti from 'canvas-confetti';
import accountService from 'modules/app-views/account/service';
import useZustand from 'hooks/useZustand';
import { onProduction } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMock = false;

const defaultFirstModal = {
  title: 'hi_name',
  description: 'tour_default_start_description',
  target: null,
};
const defaultLastModal = {
  title: 'congratulations',
  description: 'tour_default_end_description',
  target: null,
  prevButtonProps: { style: { display: 'none' } },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ApplicationTour = forwardRef(({ onTourClose, ...restProps }, ref) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedUser = useZustand('auth', 'user');
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Memoize tour steps to recalculate when language or user data changes
  const allTourSteps = useMemo(() => {
    const firstModal = storedCurrentPage?.tour?.firstModal || defaultFirstModal;
    const lastModal = storedCurrentPage?.tour?.lastModal || defaultLastModal;

    const computedFirstModal = {
      ...firstModal,
      title:
        typeof firstModal.title === 'function'
          ? firstModal.title?.(restProps)
          : translateX(firstModal.title, { name: storedUser?.name }),
      description:
        typeof firstModal.description === 'function'
          ? firstModal.description?.(restProps)
          : translateX(firstModal.description),
      nextButtonProps: {
        ...firstModal.nextButtonProps,
        autoFocus: true,
      },
    };

    const computedLastModal = {
      ...lastModal,
      title:
        typeof lastModal.title === 'function'
          ? lastModal.title?.(restProps)
          : translateX(lastModal.title),
      description:
        typeof lastModal.description === 'function'
          ? lastModal.description?.(restProps)
          : translateX(lastModal.description),
      nextButtonProps: {
        ...lastModal.nextButtonProps,
        autoFocus: true,
      },
    };

    if (isMock && !onProduction()) {
      return [computedFirstModal, computedLastModal];
    }

    return [
      // Module custom first modal
      computedFirstModal,

      // Logo: src/layouts/TopBar/Logo.jsx
      {
        title: translateX('tour_logo_title'),
        description: translateX('tour_logo_desc'),
        target: () => document.getElementById('tour-logo'),
      },

      // Navigation menu: src/layouts/Menus/TopNav.jsx
      {
        title: translateX('tour_navigation_menu_title'),
        description: translateX('tour_navigation_menu_desc'),
        target: () => document.getElementById('tour-navigation-menu'),
      },

      // Tour action button: src/layouts/TopBar/TourAction.jsx
      {
        title: translateX('tour_start_tour_title'),
        description: translateX('tour_start_tour_desc'),
        target: () => document.getElementById('tour-start-tour'),
      },

      // Settings button: src/layouts/TopBar/ApplicationSettings.jsx
      {
        title: translateX('tour_settings_title'),
        description: translateX('tour_settings_desc'),
        target: () => document.getElementById('tour-settings'),
      },

      // User account menu: src/layouts/TopBar/index.jsx
      {
        title: translateX('tour_user_account_title'),
        description: translateX('tour_user_account_desc'),
        target: () => document.getElementById('tour-user-account'),
      },

      // Welcome banner: src/modules/app-views/welcome/pages/Init/WelcomeCard.jsx
      {
        title: translateX('tour_welcome_card_title'),
        description: translateX('tour_welcome_card_desc'),
        target: () => document.getElementById('tour-welcome-card'),
      },

      // Welcome settings gear: src/modules/app-views/welcome/pages/Init/WelcomeCard.jsx
      {
        title: translateX('tour_welcome_settings_title'),
        description: translateX('tour_welcome_settings_desc'),
        target: () => document.getElementById('tour-welcome-settings'),
      },

      // Quick access modules: src/modules/app-views/welcome/pages/Init/QuickAccessModules.jsx
      {
        title: translateX('tour_quick_access_title'),
        description: translateX('tour_quick_access_desc'),
        target: () => document.getElementById('tour-quick-access'),
      },

      // Next steps - card 1 (explore): src/modules/app-views/welcome/pages/Init/NextSteps.jsx
      {
        title: translateX('tour_explore_system_title'),
        description: translateX('tour_explore_system_desc'),
        target: () => document.getElementById('tour-card-explore_the_system'),
      },

      // Next steps - card 2 (profile): src/modules/app-views/welcome/pages/Init/NextSteps.jsx
      {
        title: translateX('complete_your_profile'),
        description: translateX('tour_complete_profile_desc'),
        target: () => document.getElementById('tour-card-complete_your_profile'),
      },

      // Next steps - card 3 (subscription): src/modules/app-views/welcome/pages/Init/NextSteps.jsx
      {
        title: translateX('create_your_organization'),
        description: translateX('tour_create_organization_desc'),
        target: () => document.getElementById('tour-card-create_your_first_subscription'),
      },

      // Hide cards button: src/modules/app-views/welcome/pages/Init/NextSteps.jsx
      {
        title: translateX('tour_hide_cards_title'),
        description: translateX('tour_hide_cards_desc'),
        target: () => document.getElementById('tour-hide-cards-button'),
      },

      // Language menu: src/layouts/Footer/index.js
      {
        title: translateX('tour_language_title'),
        description: translateX('tour_language_desc'),
        target: () => document.getElementById('tour-language'),
      },

      // Switch theme: src/layouts/Footer/index.js
      {
        title: translateX('tour_switch_theme_title'),
        description: translateX('tour_switch_theme_desc'),
        target: () => document.getElementById('tour-switch-theme'),
      },

      // Application versions: src/layouts/Footer/ApplicationVersions.jsx
      {
        title: translateX('tour_app_versions_title'),
        description: translateX('tour_app_versions_desc'),
        target: () => document.getElementById('tour-app-versions'),
      },

      // Module custom last modal
      computedLastModal,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCurrentPage?.tour, storedUser?.name, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [tourOpen, setTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasFinished, setHasFinished] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  // Calculate available steps based on DOM elements (memoized)
  const availableTourSteps = useMemo(() => {
    if (!tourOpen) return [];
    return getAvailableSteps(allTourSteps);
  }, [tourOpen, allTourSteps]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleStartTour = () => {
    setTourOpen(true);
    setCurrentStep(0);
    setHasFinished(false);
  };

  const handleFinishTour = async () => {
    if (storedCurrentPage?.tour?.onFinish) {
      await storedCurrentPage?.tour?.onFinish?.();
    }
  };

  const handleTourClose = () => {
    setTourOpen(false);
    setCurrentStep(0);
    setHasFinished(false);
    if (onTourClose) {
      onTourClose();
    }
  };

  const handleStepChange = current => {
    setCurrentStep(current);
  };

  const handleTourFinish = async () => {
    // Chamado quando o usuário clica em "Finish" no último step
    // await handleFinishTour();
    handleTourClose();
  };

  // Expõe o método handleStartTour para o componente pai
  useImperativeHandle(ref, () => ({
    start: handleStartTour,
    close: handleTourClose,
  }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────
  // Listen for ESC key to close tour
  useEffect(() => {
    if (!tourOpen) return;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        handleTourClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tourOpen]);

  useEffect(() => {
    if (!tourOpen || hasFinished || availableTourSteps.length === 0) return;

    const lastAvailableStepIndex = availableTourSteps.length - 1;

    // Dispara APENAS o confetti quando chegar no último step disponível (apenas uma vez)
    if (currentStep === lastAvailableStepIndex) {
      setHasFinished(true);
      // Pequeno delay para garantir que o step final seja renderizado
      setTimeout(async () => {
        if (storedCurrentPage?.tour?.triggerConfettiOnFinish) triggerConfetti();
        await handleFinishTour();
      }, 300);
    }
  }, [currentStep, tourOpen, hasFinished, availableTourSteps]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Only render Tour when there are available steps
  if (availableTourSteps.length === 0) return null;

  return (
    <Tour
      open={tourOpen}
      onClose={handleTourClose}
      onFinish={handleTourFinish}
      onChange={handleStepChange}
      steps={availableTourSteps}
    />
  );
});

ApplicationTour.displayName = 'WelcomeInitTour';

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ApplicationTour;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAvailableSteps(steps) {
  return steps.filter(step => {
    // Steps with target: null are always available (e.g., intro and congratulations)
    if (step.target === null) return true;

    // Check if the target element exists in the DOM
    const targetElement = typeof step.target === 'function' ? step.target() : step.target;
    return targetElement !== null && targetElement !== undefined;
  });
}

function triggerConfetti() {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999,
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    });
  }

  // Multiple bursts with different configurations for realistic effect
  fire(0.25, {
    spread: 26,
    startVelocity: 55,
  });

  fire(0.2, {
    spread: 60,
  });

  fire(0.35, {
    spread: 100,
    decay: 0.91,
    scalar: 0.8,
  });

  fire(0.1, {
    spread: 120,
    startVelocity: 25,
    decay: 0.92,
    scalar: 1.2,
  });

  fire(0.1, {
    spread: 120,
    startVelocity: 45,
  });
}
